import React, { useCallback, useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ChevronRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { getData, postData } from "../../api/api";
import { useStorage } from "./../../hooks/useLocalStorage";
import { FieldError } from "../../Components/FieldError/FieldError";
import axios from "axios";
import { app, getTokens, message } from "../../configs/firebase";

// Định nghĩa mô hình xác thực cho form đăng nhập
// Sử dụng Yup để xác thực dữ liệu đầu vào
const loginMessenger = yup.object({
  // Xác thực email: bắt buộc, định dạng email hợp lệ
  email: yup
    .string()
    .trim()
    .email("Email is not valid")
    .required("Email is not blank"),
  // Xác thực mật khẩu: bắt buộc
  password: yup.string().required("Password is not blank"),
});

// Giá trị mặc định cho form đăng nhập
const LoginRequestDefault = {
  email: "",
  password: "",
};

const Login = () => {
  // Sử dụng hook lưu trữ trong localStorage để quản lý trạng thái đăng nhập
  const [token, setToken] = useStorage("accessToken", null);
  const [role, setRole] = useStorage("role", null);
  const [id, setId] = useStorage("id", null);
  const [use, setUse] = useStorage("use", null);

  // Các state quản lý trạng thái đăng nhập và lỗi
  const [LoginState, setLogin] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [LoginError, setLoginError] = useState(false);
  const [disabled, setDisabled] = useState(false);

  // Hook điều hướng của React Router
  const navigate = useNavigate();

  // State kiểm tra token (không được sử dụng trong mã nguồn)
  const [isTokenFound, setTokenFound] = useState(false);
  
  // Cấu hình form sử dụng react-hook-form và yup để xác thực
  const methods = useForm({
    resolver: yupResolver(loginMessenger), // Sử dụng Yup làm bộ xác thực
    defaultValues: LoginRequestDefault, // Giá trị mặc định của form
  });

  // Giải phóng các phương thức và trạng thái từ react-hook-form
  const {
    handleSubmit, // Hàm xử lý submit form
    register, // Đăng ký các trường input
    setFocus, // Đặt focus vào input
    formState: { errors }, // Trạng thái lỗi của form
  } = methods;

  // Thêm chức năng show password
  const [showPassword, setShowPassword] = useState(false);

  // Hàm xử lý khi click vào "Show password"
  const handleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  // Hàm xử lý khi submit form đăng nhập
  const onSubmit = (data) => {
    console.log("Form data: ", data);
    // Đặt trạng thái đăng nhập và vô hiệu hóa nút
    setLogin(false);
    setDisabled(true);

    // Gọi API đăng nhập
    postData("/auth", data)
      .then((e) => {
        // Lưu thông tin người dùng vào localStorage
        setToken(e?.accessToken);
        setRole(e?.account?.role);
        setId(e?.account?.id);
        console.log(e?.accessToken);
        setUse(e.account);
        setDisabled(false);

        // Điều hướng đến trang phù hợp dựa trên vai trò
        setTimeout(() => {
          if (e?.account?.role === "Admin") {
            navigate("/Admin/DashBoard");
          } else if (e?.account?.role === "Monk") {
            navigate("/Monk/Schedule");
          } else {
            navigate("/");
          }
        }, 300);
      })
      .catch((error) => {
        // Xử lý lỗi đăng nhập
        setDisabled(false);
        setLogin(true);
        setErrorMessage(error?.data?.message);
        console.log(error);
      });
  };

  // Hiệu ứng phụ được chạy khi component mount
  useEffect(() => {
    // Đặt focus vào ô nhập username
    setFocus("username");
    // Nếu người dùng đã đăng nhập, chuyển đến trang hồ sơ
    if (use?.id) {
      navigate("/user/profile");
    }
  }, [setFocus]);

  // Render giao diện đăng nhập
  return (
    <div
      className="flex items-center justify-center  w-screen bg-cover bg-center py-4  "
      style={{
        // Hình nền của trang đăng nhập
        backgroundImage: `url(https://i0.wp.com/plumvillage.org/wp-content/uploads/2024/10/6A2A8913.jpg?ssl=1)`,
      }}
    >
      {/* Container chính của form đăng nhập */}
      <div className="min-h-screen w-[600px] bg-gray-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        {/* Tiêu đề */}
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to your account
          </h2>
        </div>

        {/* Phần form đăng nhập */}
        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            {/* Sử dụng FormProvider của react-hook-form */}
            <FormProvider {...methods}>
              <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
                {/* Trường nhập email */}
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Email address
                  </label>
                  <div className="mt-1">
                    <input
                      id="email"
                      name="email"
                      type="text"
                      {...register("email")}
                      className={`appearance-none block w-full px-3 py-2 border ${
                        errors.email ? "border-red-500" : "border-gray-300"
                      } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 sm:text-sm`}
                    />
                    {/* Hiển thị lỗi email nếu có */}
                    {errors.email && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.email.message}
                      </p>
                    )}
                  </div>
                </div>

                {/* Trường nhập mật khẩu */}
                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Password
                  </label>
                  <div className="mt-1">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      {...register("password")}
                      className={`appearance-none block w-full px-3 py-2 border ${
                        errors.password ? "border-red-500" : "border-gray-300"
                      } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 sm:text-sm`}
                    />
                    {/* Hiển thị lỗi mật khẩu nếu có */}
                    {errors.password && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.password.message}
                      </p>
                    )}
                  </div>
                </div>

                {/* Hiển thị thông báo lỗi chung */}
                <FieldError className="text-red-500 text-center">
                  {LoginState || LoginError ? errorMessage : ""}
                </FieldError>

                {/* Phần ghi nhớ đăng nhập và quên mật khẩu */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input
                      id="show-password"
                      name="show-password"
                      type="checkbox"
                      className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                      onChange={handleShowPassword}
                    />
                    <label
                      htmlFor="show-password"
                      className="ml-2 block text-sm text-gray-900"
                    >
                      Show password
                    </label>
                  </div>

                  <div className="text-sm">
                    <Link
                      to="/forgot-password"
                      className="font-medium text-yellow-600 hover:text-yellow-500"
                    >
                      Forgot your password?
                    </Link>
                  </div>
                </div>

                {/* Nút đăng nhập */}
                <div>
                  <button
                    type="submit"
                    className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                      disabled
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                    }`}
                    disabled={disabled} // Vô hiệu hóa nút khi đang loading
                  >
                    {disabled ? "Processing..." : "Sign in"}
                  </button>
                </div>
              </form>
            </FormProvider>
          </div>
        </div>

        {/* Liên kết đăng ký tài khoản mới */}
        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md text-center">
          <p className="text-sm text-gray-600">
            Don't have an account?{" "}
            <Link
              to="/register"
              className="font-medium text-yellow-600 hover:text-yellow-500"
            >
              Register here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;